import { Edges, OrbitControls } from '@react-three/drei';
import { Text as TroikaText } from 'troika-three-text';
import { Canvas, extend, useThree } from '@react-three/fiber';
import { useEffect } from 'react';
import { BoxGeometry, EdgesGeometry, LineSegments, LineBasicMaterial } from 'three';

extend({ TroikaText, LineSegments, EdgesGeometry, LineBasicMaterial });

/**
 * 적재 예시 3D 렌더러 (로컬 버전).
 * 좌표(fitedBoxs[].coordinates, mm)는 상위에서 빈패킹 v6 결과로 계산해 넘겨준다.
 * 상품별 색상(colorMap)을 받아 같은 상품은 같은 색으로 칠해 가독성을 높인다.
 */
export function LoadExampleModalLocal({
    fitedBoxs,
    allowedSize,
    colorMap,
}) {
    return (
        <Canvas
            shadows
            camera={{ position: [1.7, 1.6, 2.2], fov: 45 }}
        >
            <MoveCamera />
            <OrbitControls
                autoRotate={false}
                enableDamping
                dampingFactor={0.12}
                minDistance={0.8}
                maxDistance={6}
                target={[0.55, 0.35, 0.55]}
            />

            {/* 조명 */}
            <ambientLight intensity={0.85} />
            <hemisphereLight args={[0xffffff, 0xcbd5e1, 0.45]} />
            <directionalLight position={[2.5, 4, 2]} intensity={0.75} castShadow />
            <directionalLight position={[-2, 2, -1.5]} intensity={0.25} />

            {/* 파레트 슬래브 */}
            <mesh position={[0.55, -0.055, 0.55]} receiveShadow>
                <boxGeometry args={[1.16, 0.11, 1.16]} />
                <meshStandardMaterial color={0xb08c66} roughness={0.85} />
            </mesh>

            {/* 적재된 상자들 */}
            {fitedBoxs?.map((fitedBox) => {
                const itemName = fitedBox.itemName;
                const color = colorMap?.get(itemName) || '#64748b';

                const width = (fitedBox?.coordinates.x2 - fitedBox?.coordinates.x1) / 1000;
                const height = (fitedBox?.coordinates.y2 - fitedBox?.coordinates.y1) / 1000;
                const depth = (fitedBox.coordinates.z2 - fitedBox.coordinates.z1) / 1000;

                const positionX = fitedBox?.coordinates.x1 / 1000 + width / 2;
                const positionY = fitedBox?.coordinates.y1 / 1000 + height / 2;
                const positionZ = fitedBox.coordinates.z1 / 1000 + depth / 2;

                return (
                    <mesh
                        key={fitedBox?.id}
                        position={[positionX, positionY, positionZ]}
                        castShadow
                        receiveShadow
                    >
                        <boxGeometry args={[width, height, depth]} />
                        <meshStandardMaterial color={color} roughness={0.55} metalness={0.05} />
                        <Edges threshold={15} color="#0f172a" />

                        <BoxLabel text={itemName} position={[0, 0, depth / 2 + 0.001]} />
                        <BoxLabel text={itemName} position={[0, 0, -depth / 2 - 0.001]} rotation={[0, Math.PI, 0]} />
                        <BoxLabel text={itemName} position={[-width / 2 - 0.001, 0, 0]} rotation={[0, Math.PI * 1.5, 0]} />
                        <BoxLabel text={itemName} position={[width / 2 + 0.001, 0, 0]} rotation={[0, Math.PI * 2.5, 0]} />
                        <BoxLabel text={itemName} position={[0, height / 2 + 0.001, 0]} rotation={[Math.PI * 1.5, 0, 0]} />
                    </mesh>
                );
            })}

            {/* 허용 규격 와이어프레임 */}
            <WireframeCube allowedSize={allowedSize} />
        </Canvas>
    );
}

// 상자 면에 붙는 라벨 (흰 글씨 + 어두운 외곽선으로 어떤 색 위에서도 읽히게 한다)
function BoxLabel({ text, position, rotation }) {
    return (
        <troikaText
            position={position}
            rotation={rotation}
            text={text}
            fontSize={0.018}
            color="#ffffff"
            outlineWidth={0.0016}
            outlineColor="#0f172a"
            maxWidth={0.9}
            anchorX="center"
            anchorY="middle"
        />
    );
}

function WireframeCube({ allowedSize }) {
    const { scene } = useThree();

    useEffect(() => {
        const w = (Number(allowedSize?.width) || 0) / 1000;
        const h = (Number(allowedSize?.height) || 0) / 1000;
        const d = (Number(allowedSize?.depth) || 0) / 1000;

        const geometry = new BoxGeometry(w, h, d);
        const edges = new EdgesGeometry(geometry);
        const material = new LineBasicMaterial({ color: 0x2563eb, transparent: true, opacity: 0.55 });
        const wireframe = new LineSegments(edges, material);
        wireframe.position.set(w / 2, h / 2, d / 2);
        scene.add(wireframe);

        return () => {
            scene.remove(wireframe);
            geometry.dispose();
            edges.dispose();
            material.dispose();
        };
    }, [scene, allowedSize]);

    return null;
}

function MoveCamera() {
    const { camera } = useThree();

    useEffect(() => {
        camera.position.set(1.7, 1.6, 2.2);
        camera.lookAt(0.55, 0.35, 0.55);
    }, [camera]);

    return null;
}
